import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Store, Send, LoaderCircle, CheckCircle2 } from "lucide-react";
import { FadeUp, MaskedLine } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const budgetOptions = [
  "Fino a € 30.000",
  "€ 30.000 – 60.000",
  "€ 60.000 – 100.000",
  "Oltre € 100.000",
  "Da definire insieme",
];

const esperienzaOptions = [
  "Ho già un'attività retail",
  "Esperienza nel settore food",
  "Prima esperienza imprenditoriale",
];

const initialForm = {
  nome: "",
  email: "",
  telefono: "",
  citta: "",
  budget: budgetOptions[0],
  esperienza: esperienzaOptions[0],
  messaggio: "",
};

const punti = [
  "Colloquio conoscitivo senza impegno",
  "Valutazione del territorio insieme al nostro team",
  "Progetto chiavi in mano e formazione inclusi",
];

const Affiliazione = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/affiliazione`, form);
      toast.success("Candidatura inviata. Ti ricontatteremo per un colloquio conoscitivo.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Qualcosa è andato storto. Riprova o scrivici via email.");
    } finally {
      setSending(false);
    }
  };

  const fieldLabel = "text-xs uppercase tracking-[0.2em] text-forest/60";
  const fieldInput =
    "mt-3 rounded-none border-0 border-b border-forest/25 bg-transparent px-0 py-3 text-base shadow-none focus-visible:border-moss focus-visible:ring-0";
  const fieldSelect =
    "mt-3 w-full rounded-none border-0 border-b border-forest/25 bg-transparent px-0 py-3 text-base text-forest focus:border-moss focus:outline-none";

  return (
    <div data-testid="affiliazione-page" className="min-h-screen pt-20">
      <Seo
        title="Candidatura Franchising Risorice | COMMPAQ"
        description="Candidati per aprire il tuo Risorice: indicaci città, budget ed esperienza. Ti ricontattiamo entro 48 ore per un colloquio conoscitivo senza impegno."
        image="/risorice/logo-icon.png"
      />
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="flex flex-col justify-between bg-forest px-6 py-20 text-bone lg:min-h-[calc(100vh-5rem)] lg:px-16 lg:py-24">
          <div>
            <p className="flex items-center gap-3 text-sm uppercase tracking-[0.3em] text-sage">
              <Store size={18} className="text-ochre" />
              Franchising Risorice
            </p>
            <h1
              data-testid="affiliazione-heading"
              className="mt-8 font-display text-5xl font-extrabold leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl"
            >
              <MaskedLine delay={0.15}>Apri il tuo</MaskedLine>
              <MaskedLine delay={0.3} className="text-moss">
                Risorice.
              </MaskedLine>
            </h1>
            <FadeUp delay={0.6}>
              <p className="mt-10 max-w-md leading-relaxed text-bone/70">
                Compila la candidatura: raccontaci chi sei, la città che hai in mente e il budget
                disponibile. Ti ricontatteremo per un colloquio conoscitivo.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.8} className="mt-16">
            <ul className="space-y-5">
              {punti.map((p) => (
                <li key={p} className="flex items-center gap-4 text-bone/80">
                  <CheckCircle2 size={20} className="shrink-0 text-moss" />
                  {p}
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>

        <div className="flex items-center px-6 py-20 lg:px-16 lg:py-24">
          <FadeUp delay={0.3} className="w-full">
            <form data-testid="affiliazione-form" onSubmit={handleSubmit} className="w-full space-y-10">
              <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                <div>
                  <label htmlFor="aff-nome" className={fieldLabel}>Nome e cognome *</label>
                  <Input
                    id="aff-nome"
                    data-testid="affiliazione-name-input"
                    required
                    value={form.nome}
                    onChange={update("nome")}
                    placeholder="Mario Rossi"
                    className={fieldInput}
                  />
                </div>
                <div>
                  <label htmlFor="aff-email" className={fieldLabel}>Email *</label>
                  <Input
                    id="aff-email"
                    type="email"
                    data-testid="affiliazione-email-input"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="mario@esempio.it"
                    className={fieldInput}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                <div>
                  <label htmlFor="aff-telefono" className={fieldLabel}>Telefono</label>
                  <Input
                    id="aff-telefono"
                    type="tel"
                    data-testid="affiliazione-phone-input"
                    value={form.telefono}
                    onChange={update("telefono")}
                    placeholder="+39 ..."
                    className={fieldInput}
                  />
                </div>
                <div>
                  <label htmlFor="aff-citta" className={fieldLabel}>Città di interesse *</label>
                  <Input
                    id="aff-citta"
                    data-testid="affiliazione-city-input"
                    required
                    value={form.citta}
                    onChange={update("citta")}
                    placeholder="Es. Bologna"
                    className={fieldInput}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                <div>
                  <label htmlFor="aff-budget" className={fieldLabel}>Budget disponibile</label>
                  <select
                    id="aff-budget"
                    data-testid="affiliazione-budget-select"
                    value={form.budget}
                    onChange={update("budget")}
                    className={fieldSelect}
                  >
                    {budgetOptions.map((b) => (
                      <option key={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="aff-esperienza" className={fieldLabel}>La tua esperienza</label>
                  <select
                    id="aff-esperienza"
                    data-testid="affiliazione-experience-select"
                    value={form.esperienza}
                    onChange={update("esperienza")}
                    className={fieldSelect}
                  >
                    {esperienzaOptions.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="aff-messaggio" className={fieldLabel}>Raccontaci la tua idea</label>
                <Textarea
                  id="aff-messaggio"
                  data-testid="affiliazione-message-input"
                  value={form.messaggio}
                  onChange={update("messaggio")}
                  placeholder="Zona, tempistiche, motivazione..."
                  rows={4}
                  className="mt-3 resize-none rounded-none border-0 border-b border-forest/25 bg-transparent px-0 py-3 text-base shadow-none focus-visible:border-moss focus-visible:ring-0"
                />
              </div>

              <Button
                type="submit"
                data-testid="affiliazione-submit-button"
                disabled={sending}
                className="group inline-flex items-center gap-3 rounded-full bg-moss px-10 py-7 text-sm uppercase tracking-[0.15em] text-bone transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-forest hover:shadow-xl hover:shadow-moss/30 disabled:opacity-60"
              >
                {sending ? (
                  <>
                    Invio in corso
                    <LoaderCircle size={16} className="animate-spin" />
                  </>
                ) : (
                  <>
                    Invia candidatura
                    <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>
          </FadeUp>
        </div>
      </div>
    </div>
  );
};

export default Affiliazione;
